import { useEffect, useState } from "react";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { projectDetailsApi } from "../api/projectDetails";
import { AssetProps } from "../components/Asset";
import { useAssetDetails } from "./useAssetDetails";

export const useUpdateAsset = ({ initialAsset, projectId, onSuccess, isEdit, existingAssets }: { initialAsset?: AssetProps, projectId: string, onSuccess: () => void; isEdit: boolean; existingAssets?: AssetProps[] }) => {

    const { removeCache } = useAssetDetails(projectId);


    const [asset, setAsset] = useState<{
        programme: AutocompleteOption | null;
        narms_value: string;
        asset_category: string | null;
        iln_number_id: string | null;
        functional_location: string;
        sap_hierarchy: string;
        asset_budget: string;
        asset_description: string;
        asset_type: string | null;
    }>({
        programme: null,
        narms_value: '',
        asset_category: null,
        iln_number_id: null,
        functional_location: '',
        sap_hierarchy: '',
        asset_budget: '',
        asset_description: '',
        asset_type: null
    });

    const [assetErrors, setAssetErrors] = useState<{ [key: string]: string }>({});
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
    const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const handleAssetChange = (key: string, value: any) => {
        setAsset((prevAsset: any) => ({
            ...prevAsset,
            [key]: value
        }));
    };

    const validateAsset = () => {
        const errors: { [key: string]: string } = {};

        if (!asset.programme) {
            errors.programme = "Programme is required.";
        }
        if (!asset.narms_value && asset.programme && asset.programme.id == 6) {
            errors.narms_value = "NARMS Value is required.";
        }
        if (!asset.asset_category) {
            errors.asset_category = "Asset Category is required.";
        }
        if (!asset.asset_type) {
            errors.asset_type = "Asset Type is required.";
        }
        if (!asset.iln_number_id) {
            errors.iln_number_id = "ILN Number ID is required.";
        }
        if (!asset.functional_location) {
            errors.functional_location = "Functional Location is required.";
        }
        if (!asset.sap_hierarchy) {
            errors.sap_hierarchy = "SAP Hierarchy is required.";
        }
        if (!asset.asset_budget) {
            errors.asset_budget = "Asset Budget is required.";
        }
        if (!asset.asset_description) {
            errors.asset_description = "Asset Description is required.";
        } 
        setAssetErrors(errors);
        return Object.keys(errors).length === 0;
    };

       const handleSubmit = async () => {
        setAssetErrors({});
        setSubmitting(true);

        if (!validateAsset()) {
            setSubmitting(false);
            return;
        }

        try {
            const payload = {
                "asset_details": [
                    {
                        "programme_id": asset?.programme?.id,
                        "asset_category": asset.asset_category,
                        "asset_type": asset.asset_type,
                        "iln_id": asset.iln_number_id,
                        "functional_location": asset.functional_location,
                        "sap_hierarchy": asset.sap_hierarchy,
                        "narms_value": Number(asset.narms_value),
                        "asset_budget": Number(asset.asset_budget),
                        "description": asset.asset_description,
                    }
                ]
            };

            if (isEdit && initialAsset) {
                const response = await projectDetailsApi.updateAsset({ projectId, assetId: initialAsset.asset_id, assetData: payload });
                if (response.status) {
                    onSuccess();
                    removeCache();
                }
            } else {
                const response = await projectDetailsApi.createAsset({ projectId, assetData: payload });
                if (response.status) {
                    onSuccess();
                    removeCache();
                }
            }
        } catch (error: any) {
            const errorCode = error?.response?.data?.data?.code;
            const errorMessage = error?.response?.data?.data?.message;

            if (errorCode === "DESC_ALREADY_EXISTS") {
                setAssetErrors((prev) => ({
                    ...prev,
                    asset_description: errorMessage || "An asset with this description already exists. Please use a different asset description.",
                }));
            } else {
                setSubmitMessage({
                    type: 'error',
                    message: error?.response?.data?.message || error?.message || (isEdit ? 'Failed to update asset. Please try again.' : 'Failed to create asset. Please try again.')
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (initialAsset && isEdit) {
            setAsset({
                programme: (initialAsset.programme_id && initialAsset.programme_name) ? { id: initialAsset.programme_id, label: initialAsset.programme_name } : null,
                asset_category: initialAsset.asset_category,
                narms_value: initialAsset.narms_value,
                iln_number_id: initialAsset.iln_id,
                functional_location: initialAsset.functional_location,
                sap_hierarchy: initialAsset.sap_hierarchy,
                asset_budget: initialAsset.asset_budget,
                asset_description: initialAsset.asset_description,
                asset_type: initialAsset.asset_type,
            });
        }
    }, [initialAsset, isEdit]);

    return {
        asset,
        assetErrors,
        handleAssetChange,
        handleSubmit,
        submitting
    }

};